import React, { useEffect, useState } from "react";
import ContactEmergency from "./ContactEmergency";
import { emergencyInfoService } from "@/services/emergency-info.service";
import type { EmergencyContactValue } from "./ContactEmergency";
import { MDialCode } from "@/components/ui/molecules/MDialCode";
import { MOtp } from "@/components/ui/molecules/MOtp";
import { otpService } from "@/services/otp.service";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getMemberInfo, useProfileUpdate } from "@/hooks/useMembers";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MAccordionHeader } from "@/components/ui/atoms/MAccordionHeader";
import { MInputField } from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { isValidEmail, isValidMobile } from "@/utils";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useTranslation } from "react-i18next";
import { RelationshipType } from "@/types/relationship";
import { goBackOneLevel } from "@/utils/nav-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import {
  checkChildStatus,
  getMemberNickFirst,
  sanitizedEmail,
} from "@/utils/member";
import {
  DerivedMemberType,
  MemberType,
  UpdateMemberContactRequest,
} from "@/types/member";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import { useNavigationSource } from "@/hooks/useNavigationSource";

const Contact: React.FC = () => {
  // OTP verification state
  const [showOtpDrawer, setShowOtpDrawer] = useState(false);
  const [otpField, setOtpField] = useState<null | "email" | "mobile">(null);
  const [otpTargetValue, setOtpTargetValue] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const memberInfo = getMemberInfo();
  const isPet =
    memberInfo.memberType === MemberType.PET ||
    memberInfo.derivedMemberType === DerivedMemberType.P;
  const isChild = checkChildStatus(memberInfo);
  const [accordion, setAccordion] = useState(() => {
    if (isPet) {
      return { emergency: true, vet: false };
    }
    const openParam = searchParams.get("open");
    return {
      myContact: openParam === "acc1",
      emergency: openParam === "emergency",
      doctor: false,
    };
  });

  // Emergency contact state
  const [emergencyValue, setEmergencyValue] = useState<EmergencyContactValue>({
    name: "",
    relationship: undefined,
    dialCode: "+91",
    mobile: "",
  });
  const [initialEmergencyValue, setInitialEmergencyValue] =
    useState<EmergencyContactValue>({
      name: "",
      relationship: undefined,
      dialCode: "+91",
      mobile: "",
    });
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [emergencyError, setEmergencyError] = useState("");
  const [emergencySuccess, setEmergencySuccess] = useState(false);

  const handleEmergencyChange = (newValue: EmergencyContactValue) => {
    setEmergencyValue((prev) => ({
      ...prev,
      ...newValue,
      relationship: newValue.relationship,
    }));
  };

  const emptyEmergency: EmergencyContactValue = {
    name: "",
    relationship: undefined,
    dialCode: "+91",
    mobile: "",
  };

  // Doctor/Vet Contact state
  const [doctorValue, setDoctorValue] = useState<EmergencyContactValue>({
    name: "",
    dialCode: "+91",
    mobile: "",
  });
  const [initialDoctorValue, setInitialDoctorValue] =
    useState<EmergencyContactValue>({
      name: "",
      dialCode: "+91",
      mobile: "",
    });
  const [doctorError, setDoctorError] = useState("");
  const [doctorSuccess, setDoctorSuccess] = useState(false);
  const [doctorLoading, setDoctorLoading] = useState(false);
  // For pets, this is Vet Contact

  const { handleContactUpdate } = useProfileUpdate();
  const [email, setEmail] = useState(memberInfo.email || "");
  const [mobile, setMobile] = useState(memberInfo.mobile || "");
  const [dialCode, setDialCode] = useState(memberInfo.dialCode || "+91");
  const [saving, setSaving] = useState(false);
  const [isDialCodePickerOpen, setIsDialCodePickerOpen] = useState(false);

  // Only relevant for non-pet members
  const isChanged =
    email !== (memberInfo.email || "") ||
    mobile !== (memberInfo.mobile || "") ||
    dialCode !== (memberInfo.dialCode || "+91");

  // Compare emergencyValue with initialEmergencyValue
  const isEmergencyChanged =
    emergencyValue.name !== initialEmergencyValue.name ||
    emergencyValue.relationship !== initialEmergencyValue.relationship ||
    emergencyValue.dialCode !== initialEmergencyValue.dialCode ||
    emergencyValue.mobile !== initialEmergencyValue.mobile;

  // Doctor Contact change detection (compare to initialDoctorValue)
  const isDoctorChanged =
    doctorValue.name !== initialDoctorValue.name ||
    doctorValue.dialCode !== initialDoctorValue.dialCode ||
    doctorValue.mobile !== initialDoctorValue.mobile;

  const getIsDoneButtonDisabled = (): boolean => {
    if (accordion.myContact) {
      const isEmailChanged = email !== (memberInfo.email || "");
      const isMobileChanged = mobile !== (memberInfo.mobile || "");
      return (
        !isChanged ||
        saving ||
        emergencyLoading ||
        doctorLoading ||
        (isEmailChanged && (!isValidEmail(email) || !isEmailVerified)) ||
        (isMobileChanged && (!isValidMobile(mobile) || !isMobileVerified))
      );
    }
    if (accordion.emergency) {
      return (
        !isEmergencyChanged ||
        saving ||
        emergencyLoading ||
        doctorLoading ||
        !isValidMobile(emergencyValue.mobile) ||
        !emergencyValue.name.trim() ||
        !emergencyValue.relationship
      );
    }
    if (accordion.doctor) {
      return (
        !isDoctorChanged ||
        saving ||
        emergencyLoading ||
        doctorLoading ||
        !isValidMobile(doctorValue.mobile)
      );
    }
    // If none open, disable
    return true;
  };

  async function handleDone() {
    // Unified save for Emergency and Doctor Contact
    if (isEmergencyChanged || isDoctorChanged) {
      setEmergencyError("");
      setEmergencySuccess(false);
      setEmergencyLoading(true);
      setDoctorError("");
      setDoctorSuccess(false);
      setDoctorLoading(true);
      try {
        const payload: any = {};
        if (isEmergencyChanged) {
          payload.family_contact = {
            name: emergencyValue.name,
            phone: `${emergencyValue.dialCode} ${emergencyValue.mobile}`.trim(),
            relationship: emergencyValue.relationship,
            otherRelation: emergencyValue.otherRelation,
          };
        }
        if (isDoctorChanged) {
          payload.pp_contact = {
            name: doctorValue.name,
            phone: `${doctorValue.dialCode} ${doctorValue.mobile}`.trim(),
          };
        }
        // Always use PUT for updates (no POST for updates)
        await emergencyInfoService.updateEmergencyInfo(memberInfo.id, payload);

        // Track completion for emergency contact
        if (isEmergencyChanged) {
          trackUserDataLoaded({
            memberInfo,
            screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
            screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CONTACT_ADD_COMPLETE,
          });
        }

        // Update state and show toast for successful updates
        if (isEmergencyChanged) {
          setEmergencySuccess(true);
          setInitialEmergencyValue(emergencyValue);
          setEmergencySuccess(false);
        }
        if (isDoctorChanged) {
          setDoctorSuccess(true);
          setInitialDoctorValue(doctorValue);
          setDoctorSuccess(false);
        }
      } catch (e: any) {
        if (isEmergencyChanged)
          setEmergencyError(e?.message || t("contact.contactUpdateError"));
        if (isDoctorChanged)
          setDoctorError(e?.message || t("contact.contactUpdateError"));
        toast.showToast({
          message: t("errors.toast"),
          variant: "error",
        });
      } finally {
        setEmergencyLoading(false);
        setDoctorLoading(false);
      }
    }

    // Save My Contact Info if changed
    if (!isChanged) {
      // Navigate back to source if exists, otherwise go back one level
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate(goBackOneLevel(location.pathname));
      }
      return;
    }
    setSaving(true);
    const updateData: UpdateMemberContactRequest = {};
    if (email) {
      updateData.email = sanitizedEmail(email);
    }
    if (mobile) {
      updateData.mobile = mobile;
      updateData.dialCode = dialCode;
    }
    const result = await handleContactUpdate({
      memberId: memberInfo.id,
      updateData,
      isChanged,
      onSavingChange: setSaving,
    });
    setSaving(false);
    if (result?.success !== false) {
      // Track completion event
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_COMPLETE,
      });

      // Navigate back to source if exists, otherwise go back one level
      const didNavigate = navigateBackIfExists();
      if (!didNavigate) {
        navigate(goBackOneLevel(location.pathname));
      }
    } else {
      toast.showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    }
  }

  // Fetch emergency info on mount/change
  useEffect(() => {
    let ignore = false;
    async function fetchEmergencyInfo() {
      setEmergencyLoading(true);
      setEmergencyError("");
      setEmergencySuccess(false);
      try {
        const res = await emergencyInfoService.getEmergencyInfo(memberInfo.id);
        if (!ignore) {
          // Emergency Contact
          if (res?.family_contact) {
            const loaded: EmergencyContactValue = {
              name: res.family_contact.name || "",
              relationship:
                res.family_contact.relationship || RelationshipType.SPOUSE,
              otherRelation: res.family_contact.otherRelation || "",
              dialCode: (res.family_contact.phone || "").split(" ")[0] || "+91",
              mobile:
                (res.family_contact.phone || "")
                  .split(" ")
                  .slice(1)
                  .join(" ") || "",
            };
            setEmergencyValue(loaded);
            setInitialEmergencyValue(loaded);
          } else {
            setEmergencyValue(emptyEmergency);
            setInitialEmergencyValue(emptyEmergency);
          }

          // Doctor Contact
          if (res?.pp_contact) {
            const doctorLoaded: EmergencyContactValue = {
              name: res.pp_contact.name || "",
              dialCode: (res.pp_contact.phone || "").split(" ")[0] || "+91",
              mobile:
                (res.pp_contact.phone || "").split(" ").slice(1).join(" ") ||
                "",
            };
            setDoctorValue(doctorLoaded);
            setInitialDoctorValue(doctorLoaded);
          } else {
            const emptyDoctor = { name: "", dialCode: "+91", mobile: "" };
            setDoctorValue(emptyDoctor);
            setInitialDoctorValue(emptyDoctor);
          }
        }
      } catch (e: any) {
        setEmergencyError(e?.message || "Failed to fetch emergency info");
        setEmergencyValue(emptyEmergency);
        setInitialEmergencyValue(emptyEmergency);
        setDoctorValue({ name: "", dialCode: "+91", mobile: "" });
      } finally {
        setEmergencyLoading(false);
      }
    }
    fetchEmergencyInfo();
    return () => {
      ignore = true;
    };
  }, [memberInfo.id]);

  // Track GTM user_data_loaded event
  useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_INFO_CONTACT_EDIT,
    });
  }, [memberInfo?.id]);

  const handleSendMobileOtp = async () => {
    setOtpLoading(true);
    setOtpField("mobile");
    setOtpTargetValue(mobile);
    setShowOtpDrawer(true);

    // Track mobile verification event
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName:
        ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_VERIFY_MOBILE,
    });

    try {
      await otpService.sendOtp({
        send_to: mobile,
        dialCode,
        mode: "sms",
        type: "member_update",
      });
    } catch (err: any) {
      toast.showToast({
        message: t("errors.toast"),
        variant: "error",
      });
      setShowOtpDrawer(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleSendEmailOtp = async () => {
    setOtpLoading(true);
    setOtpField("email");
    setOtpTargetValue(email);
    setShowOtpDrawer(true);

    // Track email verification event
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
      screenName:
        ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_VERIFY_EMAIL,
    });

    try {
      await otpService.sendOtp({
        send_to: email,
        mode: "email",
        type: "member_update",
      });
    } catch (err: any) {
      toast.showToast({
        message: t("errors.toast"),
        variant: "error",
      });
      setShowOtpDrawer(false);
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpVerify = async (otpValue: string): Promise<true | string> => {
    setOtpLoading(true);
    try {
      const response = await otpService.verifyOtp({
        send_to: otpTargetValue,
        dialCode: otpField === "mobile" ? dialCode : undefined,
        mode: otpField === "mobile" ? "sms" : "email",
        type: "member_update",
        otp: otpValue,
      });

      // check if the verified send_to matches the current target (case-insensitive for email)
      const expectedTarget =
        otpField === "email"
          ? otpTargetValue.toLowerCase().trim()
          : otpTargetValue;
      const receivedTarget = response.data?.target || "";

      if (!(response.data?.verified && receivedTarget === expectedTarget)) {
        console.error("OTP verification failed: target mismatch", {
          expected: expectedTarget,
          received: receivedTarget,
        });
        return "Verification failed: target mismatch";
      }

      // Success: track completion event
      if (otpField === "mobile") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
          screenName:
            ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_VERIFY_MOBILE_COMPLETE,
        });
      } else if (otpField === "email") {
        trackUserDataLoaded({
          memberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
          screenName:
            ANALYTICS_SCREEN_NAMES.PROFILE_BASIC_INFO_CONTACT_VERIFY_EMAIL_COMPLETE,
        });
      }

      // Update state and return true for MOtp
      setShowOtpDrawer(false);
      if (otpField === "email") setIsEmailVerified(true);
      if (otpField === "mobile") setIsMobileVerified(true);
      setTimeout(() => setOtpField(null), 250);
      return true;
    } catch (err: any) {
      // Handle wrong OTP specifically
      const status = err?.response?.status;
      if (status === 400) {
        return "The otp you entered is incorrect. Please try again.";
      }
      return err?.message || "Verification failed";
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpResend = async () => {
    setOtpLoading(true);
    try {
      await otpService.sendOtp({
        send_to: otpTargetValue,
        dialCode: otpField === "mobile" ? dialCode : undefined,
        mode: otpField === "mobile" ? "sms" : "email",
        type: "member_update",
      });
      // Optionally show a toast for resend success
    } catch (err: any) {
      // Optionally show a toast for resend failure
      toast.showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const getLearnMoreContent = () => {
    return (
      <p>
        {t("contact.learnMoreText1")}
        {!memberInfo.onBehalf && (
          <>
            <br />
            <br />
            {t("contact.learnMoreText2")}
          </>
        )}
        <br />
        <br />
        {t("contact.learnMoreText3")}
      </p>
    );
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader module="contact" memberInfo={memberInfo} />
      <MSpacer mt={24} />
      <div className="flex-1">
        {/* Emergency Contact */}
        <div className="mb-3">
          {!accordion.emergency ? (
            <MAccordionHeader
              title={t("contact.yourEmergencyContact")}
              isOpen={accordion.emergency}
              variant="contact"
              onClick={() => {
                // Track when emergency contact accordion is opened
                trackUserDataLoaded({
                  memberInfo,
                  screenType: ANALYTICS_SCREEN_TYPES.PROFILE,
                  screenName: ANALYTICS_SCREEN_NAMES.EMERGENCY_CONTACT_ADD,
                });

                setAccordion({
                  myContact: false,
                  emergency: true,
                  doctor: false,
                });
              }}
              className="accordion-header-16"
            />
          ) : (
            <div
              style={{
                display: "flex",
                padding: "var(--scale-padding-lg, 16px)",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "24px",
                alignSelf: "stretch",
                borderRadius: "8px",
                border: "1px solid var(--colour-border-divider-n40, #E2E2E2)",
                background: "var(--colour-background-surface-lvl-2, #FFF)",
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.04)",
              }}
            >
              <MAccordionHeader
                title={t("contact.yourEmergencyContact")}
                isOpen={accordion.emergency}
                variant="contact"
                onClick={() => {
                  setAccordion({
                    myContact: false,
                    emergency: true,
                    doctor: false,
                  });
                }}
                className="accordion-header-16"
              />
              <ContactEmergency
                value={emergencyValue}
                onChange={handleEmergencyChange}
                error={emergencyError}
                success={emergencySuccess}
                disabled={emergencyLoading}
                showRelationship={!isPet}
              />
            </div>
          )}
        </div>
        {/* Vet/Doctor Contact */}
        <div className="mb-3">
          {!(isPet ? accordion.vet : accordion.doctor) ? (
            <MAccordionHeader
              title={
                isPet
                  ? t("contact.yourVetContact")
                  : t("contact.yourDoctorContact")
              }
              isOpen={isPet ? accordion.vet : accordion.doctor}
              variant="contact"
              onClick={() => {
                if (isPet) {
                  setAccordion({ emergency: false, vet: true });
                } else {
                  setAccordion({
                    myContact: false,
                    emergency: false,
                    doctor: true,
                  });
                }
              }}
              className="accordion-header-16"
            />
          ) : (
            <div
              style={{
                display: "flex",
                padding: "var(--scale-padding-lg, 16px)",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: "24px",
                alignSelf: "stretch",
                borderRadius: "8px",
                border: "1px solid var(--colour-border-divider-n40, #E2E2E2)",
                background: "var(--colour-background-surface-lvl-2, #FFF)",
                boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.04)",
              }}
            >
              <MAccordionHeader
                title={
                  isPet
                    ? t("contact.yourVetContact")
                    : t("contact.yourDoctorContact")
                }
                isOpen={isPet ? accordion.vet : accordion.doctor}
                variant="contact"
                onClick={() => {
                  if (isPet) {
                    setAccordion({ emergency: false, vet: true });
                  } else {
                    setAccordion({
                      myContact: false,
                      emergency: false,
                      doctor: true,
                    });
                  }
                }}
                className="accordion-header-16"
              />
              <ContactEmergency
                value={doctorValue}
                onChange={setDoctorValue}
                error={doctorError}
                success={doctorSuccess}
                disabled={doctorLoading}
                showRelationship={false}
                contactType="doctor"
                isPet={isPet}
              />
            </div>
          )}
        </div>
      </div>
      {/* Fixed Done Button */}
      <div className="confirm-footer">
        <MButton
          size="lg"
          variant="filled"
          className="w-full"
          disabled={getIsDoneButtonDisabled()}
          onClick={handleDone}
          text={t("common.done")}
        />
      </div>
      {/* OTP Drawer */}
      <MOtp
        open={showOtpDrawer}
        onClose={() => setShowOtpDrawer(false)}
        onDone={handleOtpVerify}
        onResend={handleOtpResend}
        onEdit={() => {
          setShowOtpDrawer(false);
        }}
        sendTo={otpTargetValue}
        loading={otpLoading}
        title={`Verify ${otpField === "email" ? "Email" : "Mobile"}`}
        resendTimer={30}
      />
    </div>
  );
};

export default Contact;
